import { Card } from "@/components/flexkit/Card";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import { Text } from "@/components/podkit/typography/Text";
import { Switch } from "@/components/Switch";
import { TAX_ID_TYPES, TAX_ID_TYPES_BY_ID } from "@/routes/settings/organization/billing/stripe-tax-ids";
import type { FC } from "react";

export type BusinessVATData = {
    isPurchasingAsBusiness: boolean;
    taxIdTypeId: string;
    taxIdValue: string;
};

type BusinessVATSectionProps = {
    data: BusinessVATData;
    onDataChange: (data: BusinessVATData) => void;
};

export const BusinessVATSection: FC<BusinessVATSectionProps> = ({ data, onDataChange }) => {
    const { isPurchasingAsBusiness, taxIdTypeId, taxIdValue } = data;

    const handleBusinessCheckboxChange = (checked: boolean) => {
        onDataChange({
            ...data,
            isPurchasingAsBusiness: checked,
            // Reset tax ID fields when unchecking business
            taxIdTypeId: checked ? taxIdTypeId : "",
            taxIdValue: checked ? taxIdValue : "",
        });
    };

    const handleTaxIdTypeChange = (value: string) => {
        onDataChange({
            ...data,
            taxIdTypeId: value,
        });
    };

    const handleTaxIdValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onDataChange({
            ...data,
            taxIdValue: e.target.value,
        });
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="mb-2 flex items-center gap-3">
                <Switch
                    state={isPurchasingAsBusiness ? "checked" : "unchecked"}
                    onToggle={handleBusinessCheckboxChange}
                    id="purchase-as-business-toggle"
                />
                <Text className="text-base font-medium">As a business</Text>
            </div>

            {isPurchasingAsBusiness && (
                <Card className="flex flex-col gap-2 rounded-md bg-surface-glass shadow-sm" variant="bordered">
                    <label htmlFor="vat-info" className="text-content-primary">
                        VAT information
                    </label>

                    <div className="flex flex-row gap-2">
                        <div className="flex w-fit">
                            <Select value={taxIdTypeId} onValueChange={handleTaxIdTypeChange}>
                                <SelectTrigger id="tax-id-type" className="w-full" loading={false}>
                                    <SelectValue placeholder="Select tax ID type">
                                        <div className="text-content-primary">
                                            {TAX_ID_TYPES_BY_ID[taxIdTypeId]?.label}
                                        </div>
                                    </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                    {TAX_ID_TYPES.map(({ id, label, country }) => (
                                        <SelectItem key={id} value={id}>
                                            <div className="flex flex-col gap-1">
                                                <div className="text-sm text-content-secondary">{country}</div>
                                                <div className="text-content-primary">{label}</div>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <InputField id="tax-id-value">
                            <Input
                                id="tax-id-value"
                                placeholder="VAT ID"
                                value={taxIdValue}
                                onChange={handleTaxIdValueChange}
                                className="w-full"
                                required
                            />
                        </InputField>
                    </div>
                </Card>
            )}
        </div>
    );
};
